import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

export interface ImageUsageStats {
  imagesUsed: number;
  imagesLimit: number;
  lastResetDate: string;
  usagePercentage: number;
  isOverLimit: boolean;
  // Additional statistics
  lastActiveAt?: string;
  totalImagesProcessed?: number;
  imagesUsedAtLastPurchase?: number;
  showRemainingView?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ImageUsageService {
  private translationService = inject(TranslationService);
  private currentUserId: string = 'anonymous';
  private hasLoggedUnauthenticated = false;
  private isInitialized = false;
  private isLoading = false; // Flag to prevent concurrent loading calls
  private isLoadingStats = false; // Flag to prevent concurrent stats loading

  private usageStatsSubject = new BehaviorSubject<ImageUsageStats>(
    this.getDefaultStats()
  );
  public usageStats$ = this.usageStatsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    console.log('🖼️ ImageUsageService constructor called');

    // Initialize with defaults
    const defaultStats = this.getDefaultStats();
    console.log('📊 Initializing with default image stats:', defaultStats);
    this.usageStatsSubject.next(defaultStats);

    // Subscribe to authentication changes
    this.authService.currentUser$.subscribe((user: User | null) => {
      if (user && !this.isInitialized) {
        console.log('👤 User logged in, loading image data...');
        this.isInitialized = false;
        this.checkAuthAndLoadData();
      } else if (!user) {
        console.log('👤 User logged out, resetting image data to defaults...');
        this.isInitialized = false;
        this.usageStatsSubject.next(this.getDefaultStats());
      }
    });

    // Check if user is authenticated before trying to load from API
    this.checkAuthAndLoadData();
  }

  private async checkAuthAndLoadData(): Promise<void> {
    if (this.isInitialized) {
      console.log('🔐 ImageUsageService: Already initialized, skipping...');
      return;
    }

    // Prevent concurrent calls
    if (this.isLoading) {
      console.log('🔐 ImageUsageService: Already loading, skipping duplicate call...');
      return;
    }

    this.isLoading = true;

    try {
      console.log('🔐 ImageUsageService: Checking authentication...');
      const user = await this.authService.getCurrentUser();

      if (!user) {
        if (!this.hasLoggedUnauthenticated) {
          console.log(
            '🔐 ImageUsageService: User not authenticated, using defaults'
          );
          this.hasLoggedUnauthenticated = true;
        }
        return;
      }

      console.log(
        '✅ ImageUsageService: User authenticated, loading data from API'
      );
      this.currentUserId = user.email || 'anonymous';
      this.isInitialized = true;

      // Load data with delay for mobile
      const isMobile = Capacitor.isNativePlatform();
      const delay = isMobile ? 300 : 0;

      setTimeout(() => {
        this.loadImageUsageFromAPI();
      }, delay);
    } catch (error) {
      console.error('❌ ImageUsageService: Error checking auth:', error);
      this.isInitialized = true; // Mark as initialized even on error
    } finally {
      this.isLoading = false; // Always reset loading flag
    }
  }

  private getDefaultStats(): ImageUsageStats {
    return {
      imagesUsed: 0,
      imagesLimit: 2, // Default 2 images for new users
      lastResetDate: new Date().toISOString(),
      usagePercentage: 0,
      isOverLimit: false,
      totalImagesProcessed: 0,
      imagesUsedAtLastPurchase: 0,
      showRemainingView: true,
    };
  }

  public async loadImageUsageFromAPI(): Promise<void> {
    // Prevent concurrent calls
    if (this.isLoadingStats) {
      console.log('🔐 ImageUsageService: Stats already loading, skipping duplicate call...');
      return;
    }

    this.isLoadingStats = true;

    try {
      console.log('🔄 Loading image usage from API...');
      const user = await this.authService.getCurrentUser();

      if (!user) {
        console.log('⚠️ No authenticated user, cannot load image usage');
        this.isLoadingStats = false;
        return;
      }

      const headers = await this.getAuthHeaders();
      const url = `${environment.apiUrl}/api/image-usage`;

      this.http
        .get<ImageUsageStats>(url, { headers, withCredentials: true })
        .pipe(
          tap((stats) => {
            console.log('✅ Image usage loaded from API:', stats);
            // Handle NaN values in imagesLimit
            if (
              typeof stats.imagesLimit === 'number' &&
              isNaN(stats.imagesLimit)
            ) {
              console.warn('⚠️ Detected NaN imagesLimit, setting to default 2');
              stats.imagesLimit = 2;
            }
            this.usageStatsSubject.next(stats);
          }),
          catchError((error) => {
            console.error('❌ Error loading image usage:', error);
            // Keep using default stats on error
            return throwError(() => error);
          })
        )
        .subscribe({
          complete: () => {
            this.isLoadingStats = false; // Reset flag when subscription completes
          },
          error: () => {
            this.isLoadingStats = false; // Reset flag on error
          },
        });
    } catch (error) {
      console.error('❌ Error in loadImageUsageFromAPI:', error);
      this.isLoadingStats = false; // Reset flag on exception
    }
  }

  private async getAuthHeaders(): Promise<HttpHeaders> {
    const token = await this.authService.getAuthToken();
    const isMobile = Capacitor.isNativePlatform();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (token) {
      console.log(
        '🔐 ImageUsageService: Got auth token, setting Authorization header'
      );
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      console.warn('⚠️ ImageUsageService: No auth token available');
    }

    return headers;
  }

  public async incrementImageUsage(): Promise<boolean> {
    try {
      console.log('📊 Incrementing image usage...');
      const user = await this.authService.getCurrentUser();

      if (!user) {
        console.log('⚠️ No authenticated user, cannot increment image usage');
        return false;
      }

      const headers = await this.getAuthHeaders();
      const url = `${environment.apiUrl}/api/image-usage/increment`;

      const result = await this.http
        .post<ImageUsageStats>(url, {}, { headers, withCredentials: true })
        .pipe(
          tap((stats) => {
            console.log('✅ Image usage incremented:', stats);
            // Handle NaN values in imagesLimit
            if (
              typeof stats.imagesLimit === 'number' &&
              isNaN(stats.imagesLimit)
            ) {
              console.warn('⚠️ Detected NaN imagesLimit, setting to default 2');
              stats.imagesLimit = 2;
            }
            this.usageStatsSubject.next(stats);
          }),
          catchError((error) => {
            console.error('❌ Error incrementing image usage:', error);
            return throwError(() => error);
          })
        )
        .toPromise();

      return true;
    } catch (error) {
      console.error('❌ Error in incrementImageUsage:', error);
      return false;
    }
  }

  public async checkImageLimit(): Promise<boolean> {
    const currentStats = this.usageStatsSubject.value;
    console.log('🔍 Checking image limit:', currentStats);

    if (currentStats.imagesUsed >= currentStats.imagesLimit) {
      console.log('❌ Image limit exceeded');
      await this.toastService.showWarning(
        'Image limit reached. Please purchase more images to continue.',
        3000
      );
      return false;
    }

    return true;
  }

  public getCurrentStats(): ImageUsageStats {
    return this.usageStatsSubject.value;
  }

  public async purchaseImages(imageCount: number): Promise<boolean> {
    try {
      console.log(`💰 Purchasing ${imageCount} images from backend API...`);
      const user = await this.authService.getCurrentUser();

      if (!user) {
        console.log('⚠️ No authenticated user, cannot purchase images');
        return false;
      }

      const headers = await this.getAuthHeaders();
      const url = `${environment.apiUrl}/api/image-usage/purchase`;

      const result = await this.http
        .post<ImageUsageStats>(
          url,
          { imageCount },
          { headers, withCredentials: true }
        )
        .pipe(
          tap((stats) => {
            console.log('✅ Images purchased successfully, updating from backend API response:', stats);
            // Handle NaN values in imagesLimit
            if (
              typeof stats.imagesLimit === 'number' &&
              isNaN(stats.imagesLimit)
            ) {
              console.warn('⚠️ Detected NaN imagesLimit, setting to default 2');
              stats.imagesLimit = 2;
            }
            // Update stats from API response (this is fresh data from backend)
            this.usageStatsSubject.next(stats);
            
            this.toastService.showSuccess(
              this.translationService.formatMessage(
                'imagesAddedSuccessfully',
                imageCount
              ),
              3000
            );
          }),
          catchError((error) => {
            console.error('❌ Error purchasing images:', error);
            this.toastService.showError(
              'Failed to purchase images. Please try again.',
              3000
            );
            return throwError(() => error);
          })
        )
        .toPromise();

      return true;
    } catch (error) {
      console.error('❌ Error in purchaseImages:', error);
      return false;
    }
  }

  public async toggleView(): Promise<void> {
    const currentStats = this.usageStatsSubject.value;
    const newStats = {
      ...currentStats,
      showRemainingView: !currentStats.showRemainingView,
    };
    this.usageStatsSubject.next(newStats);

    // Save preference to backend
    try {
      const headers = await this.getAuthHeaders();
      const url = `${environment.apiUrl}/api/image-usage/toggle-view`;

      await this.http
        .post(
          url,
          { showRemainingView: newStats.showRemainingView },
          { headers, withCredentials: true }
        )
        .toPromise();
    } catch (error) {
      console.error('❌ Error toggling view:', error);
    }
  }

  public forceRefreshData(): void {
    console.log('🔄 Force refreshing image usage data from backend API...');
    
    // Reset initialization flags to force fresh API call
    this.isInitialized = false;
    this.isLoading = false;
    this.isLoadingStats = false;
    
    // Directly call API method to fetch fresh data
    this.loadImageUsageFromAPI();
  }

  // Confirm Stripe payment and update user quotas
  confirmStripePayment(paymentIntentId: string): Observable<any> {
    console.log('💳 Confirming Stripe payment:', paymentIntentId);

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${environment.apiUrl}/api/stripe/confirm-payment`,
          { paymentIntentId },
          { headers }
        );
      }),
      tap((response) => {
        if (response.success) {
          console.log(
            '✅ Stripe payment confirmed successfully:',
            response.data
          );
          if (response.quotaSettings) {
            const currentStats = this.usageStatsSubject.value;
            this.usageStatsSubject.next({
              ...currentStats,
              imagesLimit:
                response.quotaSettings.imageLimit ?? currentStats.imagesLimit,
            });
          }
          // Reload usage stats to reflect the new limits
          this.loadImageUsageFromAPI();
        }
      }),
      catchError((error) => {
        console.error('❌ Error confirming Stripe payment:', error);
        return throwError(() => error);
      })
    );
  }
}
